import React, { useState, useEffect } from 'react';
import { vocabularyAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Search, Plus, Trash2, Edit2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminVocabulary() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ term: '', synonyms: '', definition: '', category: '' });

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await vocabularyAPI.list({ search: search || undefined });
      setTerms(res.data.terms || []);
    } catch { toast.error('Failed to load vocabulary'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ term: '', synonyms: '', definition: '', category: '' });
    setShowDialog(true);
  };

  const openEdit = (term) => {
    setEditingId(term.id);
    setForm({
      term: term.term || '',
      synonyms: (term.synonyms || []).join(', '),
      definition: term.definition || '',
      category: term.category || '',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.term.trim()) { toast.error('Term is required'); return; }
    const payload = {
      term: form.term.trim(),
      synonyms: form.synonyms ? form.synonyms.split(',').map(s => s.trim()).filter(Boolean) : [],
      definition: form.definition || null,
      category: form.category || null,
    };
    try {
      if (editingId) {
        await vocabularyAPI.update(editingId, payload);
        toast.success('Term updated');
      } else {
        await vocabularyAPI.create(payload);
        toast.success('Term added');
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save term');
    }
  };

  const handleDelete = async (id) => {
    try {
      await vocabularyAPI.delete(id);
      toast.success('Term deleted');
      load();
    } catch { toast.error('Failed to delete term'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BookOpen className="w-6 h-6 text-[#B89B5E]" /> Controlled Vocabulary</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage canonical terms and synonyms for consistent tagging</p>
        </div>
        <Button onClick={openCreate} className="bg-[#121316] text-white hover:bg-[#1B1D22]">
          <Plus className="w-4 h-4 mr-2" /> Add Term
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B616E]" />
        <Input className="pl-10" placeholder="Search terms and synonyms..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : terms.length === 0 ? (
        <div className="text-center py-16 text-[#5B616E]">
          <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg">No vocabulary terms</p>
          <p className="text-sm mt-1">Add canonical terms with synonyms to improve tagging consistency</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {terms.map(t => (
            <Card key={t.id} className="bg-white border-[#E7E0D6]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-sm">{t.term}</h3>
                    {t.category && <Badge variant="outline" className="text-[10px] mt-0.5">{t.category}</Badge>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(t)}><Edit2 className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => handleDelete(t.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                {t.definition && <p className="text-xs text-[#5B616E] mb-2 line-clamp-2">{t.definition}</p>}
                {(t.synonyms || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {t.synonyms.map((s, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px]">{s}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Term' : 'Add Vocabulary Term'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Canonical term *</label>
              <Input value={form.term} onChange={e => setForm(f => ({ ...f, term: e.target.value }))} placeholder="e.g. artificial intelligence" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Synonyms (comma separated)</label>
              <Input value={form.synonyms} onChange={e => setForm(f => ({ ...f, synonyms: e.target.value }))} placeholder="e.g. AI, machine learning, ML" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Definition</label>
              <Textarea rows={3} value={form.definition} onChange={e => setForm(f => ({ ...f, definition: e.target.value }))} placeholder="Brief definition..." />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Category / topic area</label>
              <Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. technology, politics, health" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#121316] text-white hover:bg-[#1B1D22]">{editingId ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
